// useTenantUsage
// ---------------
// Shared hook that surfaces the tenant's account-level facts — the seat limit
// (Users page) and the tier label (Billing page) — to every surface that shows
// them.
//
// One source of truth so those surfaces can't disagree.
//
// Scope note: quota is per web app, so per-app pentest cues read
// useTenantEntitlements, NOT this tenant-wide aggregate.
//
// Reads cache-then-refresh, the same strategy useTenantEntitlements uses. These
// values are stable tenant facts that only the Client Owner can change, from a
// single place, so a cached copy can't go meaningfully stale between page
// views — and warmTenantUsageCache() has usually populated that cache during
// the idle window after login. Without it, both consuming pages painted a
// "no seat limit" phrasing for the instant before the read landed and then
// visibly corrected themselves.
//
// hasLoaded distinguishes "no data yet" from "loaded, and the answer is
// unlimited" — both leave usage null-ish for the seat limit, but only the
// second may be stated to the user as fact.

import { useCallback, useEffect, useState } from "react";
import {
  deriveUsageView,
  fetchTenantUsage,
  TenantUsage,
  TenantUsageView,
} from "@/services/accountUsage.service";
import { CacheKey } from "@/constants/definitions";
import { cacheThenRefresh } from "@/utils/cache";

interface UseTenantUsageResult {
  usage: TenantUsageView | null;
  isLoading: boolean;
  // True once a value has been applied from EITHER the cache or the network.
  // Gate any copy that asserts an absent limit on this, not on !isLoading.
  hasLoaded: boolean;
  error: string | null;
  refresh: () => void;
}

export function useTenantUsage(): UseTenantUsageResult {
  const [usage, setUsage] = useState<TenantUsageView | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshCounter, setRefreshCounter] = useState<number>(0);

  useEffect(() => {
    let cancelled = false;
    // Tracks whether anything has been rendered yet this pass, so a failed
    // fetch that still has cached data behind it doesn't replace real values
    // with an error banner.
    let hasAppliedUsage = false;
    setIsLoading(true);
    setError(null);

    const applyUsage = (fetched: TenantUsage) => {
      if (cancelled) return;
      hasAppliedUsage = true;
      setUsage(deriveUsageView(fetched));
      setIsLoading(false);
    };

    const applyError = (fetchError: unknown) => {
      if (cancelled) return;
      setIsLoading(false);
      // Cached values are already on screen — an error over the top of them
      // would be misleading. The next refresh gets another go.
      if (hasAppliedUsage) return;
      setError(
        fetchError instanceof Error
          ? fetchError.message
          : "Failed to load tenant usage",
      );
    };

    // An explicit refresh() skips the cache: re-applying it first would flash
    // the pre-change values the caller is trying to move on from.
    const isInitialLoad = refreshCounter === 0;
    if (isInitialLoad) {
      cacheThenRefresh<TenantUsage>(
        CacheKey.getTenantUsage,
        fetchTenantUsage,
        applyUsage,
        { onError: applyError },
      );
    } else {
      fetchTenantUsage().then(applyUsage).catch(applyError);
    }

    return () => {
      cancelled = true;
    };
  }, [refreshCounter]);

  const refresh = useCallback(
    () => setRefreshCounter((current) => current + 1),
    [],
  );

  return { usage, isLoading, hasLoaded: usage !== null, error, refresh };
}
